import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as common from '../common'
import * as rules from '../rules'

import * as gql from './gql'
import type { Context, TypeParams } from './types'

/** Quantity of records to fetch in one chunk */
const GL_RECORD_CHUNK_QUANTITY = 1000

/** Maximum number of GL record chunks to fetch in parallel */
const GL_RECORD_CHUNK_CONCURRENCY = 4

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  private readonly _rulesEc = ngCore.inject(rules.Collection)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      // NOTE: There is nothing to fetch if there is no context
      if (!context) {
        return rxjs.of([])
      }

      // Utilize pagination if its available
      if (
        request?.pagination &&
        ents.core.common.Request.For.Pagination.ByPosition.isPaginated(
          request.pagination,
        )
      ) {
        return gql.queries.records.executor({
          ...context,
          filter: common.gql.filters.inputs.filter.TRANSFORMER.getGqlFrom(
            request?.filter?.additionalFilters,
          ),
          quantity: request?.pagination?.quantity,
          skip: request?.pagination?.skip,
        })
      } else {
        // If pagination is not available, fetch all records in smaller chunks
        return this._fetchGlRecordsInChunks(context, request)
      }
    },
  )

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    context => {
      const rulesLookup$ =
        this._rulesEc.plugins.cachePlugin.feature.selectors.lookup.asObservable
      return context
        ? gql.queries.attributes.executor(context).pipe(
            rxjs.switchMap(attributes =>
              rulesLookup$.pipe(
                rxjs.map(rulesLookup =>
                  _getFullAttributes(
                    attributes.map(attribute => ({
                      ...attribute,
                      // Finds the given attribute in the record
                      getValueFrom: record =>
                        record.attributes?.find(
                          attributeValue => attributeValue.id === attribute.id,
                        )?.value,
                    })),
                    // NOTE: this is all just to address the mixture of
                    // string/number IDs (string IDs on the frontend, number IDs
                    // on the backend)
                    rulesLookup,
                  ),
                ),
              ),
            ),
          )
        : rxjs.of([])
    }

  /** @inheritdoc */
  public count: ec.models.Api<TypeParams>['count'] = (context, request) =>
    context
      ? gql.queries.recordCount.executor({
          ...context,
          filter: common.gql.filters.inputs.filter.TRANSFORMER.getGqlFrom(
            request?.filter?.additionalFilters,
          ),
        })
      : rxjs.of(0)

  /**
   * Fetch all GL records when the caller does not provide pagination.
   *
   * The backend query is still paginated, so we first ask for the filtered
   * count and then request fixed-size pages in ordered parallel batches.
   *
   * Chunk size is defined by {@link GL_RECORD_CHUNK_QUANTITY}; batch
   * concurrency is defined by {@link GL_RECORD_CHUNK_CONCURRENCY}.
   */
  private _fetchGlRecordsInChunks(
    context: NonNullable<Context>,
    request?: Pick<ec.models.QueryRequest<TypeParams>, 'filter'>,
  ): rxjs.Observable<ents.extra.rate.gl.Record[]> {
    const filter = common.gql.filters.inputs.filter.TRANSFORMER.getGqlFrom(
      request?.filter?.additionalFilters,
    )
    return gql.queries.recordCount.executor({ ...context, filter }).pipe(
      rxjs.switchMap(recordCount => {
        // Calculate the number of pages needed to fetch all records
        const pageCount = Math.ceil(
          (recordCount ?? 0) / GL_RECORD_CHUNK_QUANTITY,
        )
        if (pageCount === 0) {
          return rxjs.of([])
        }
        // Create an observable that emits page indexes, then buffer them into
        // batches, and fetch each batch in parallel. Finally, concatenate the
        // results into a single array
        return rxjs.range(0, pageCount).pipe(
          rxjs.bufferCount(GL_RECORD_CHUNK_CONCURRENCY),
          // Each batch runs in parallel. Batches run in sequence so the final
          // array remains in page order
          rxjs.concatMap(pageIndexes =>
            rxjs.forkJoin(
              pageIndexes.map((pageIndex: number) =>
                gql.queries.records.executor({
                  ...context,
                  filter,
                  quantity: GL_RECORD_CHUNK_QUANTITY,
                  skip: pageIndex * GL_RECORD_CHUNK_QUANTITY,
                }),
              ),
            ),
          ),
          rxjs.concatMap(batchRecords => batchRecords.flat()),
          rxjs.toArray(),
        )
      }),
    )
  }
}

function _getFullAttributes(
  attributes: readonly ec.models.Attribute<TypeParams>[],
  rulesLookup: Parameters<typeof ents.extra.rate.gl.Record.getMapping>[1],
): ec.models.Attribute<TypeParams>[] {
  return [
    // We show categorization attributes first because they are very
    // relevant
    ...common.attributes.mapping.getIdcCategorizationAttributes<
      TypeParams['entity']
    >(record => ents.extra.rate.gl.Record.getMapping(record, rulesLookup)),
    // Then we show all of the included attributes
    ...attributes,
    // Finally, we show the label attributes since they are least relevant
    ...common.attributes.mapping.getLabelAttributes<TypeParams['entity']>(
      record => ents.extra.rate.gl.Record.getMapping(record, rulesLookup),
    ),
  ]
}
